"use client";

import { Box, Flex, Heading, Link, Text } from "@chakra-ui/react";
import { useState } from "react";
import { LuArrowUp } from "react-icons/lu";
import NextLink from "next/link";

import { Magnetic } from "@components/common/Magnetic";
import { MarqueeStrip } from "@components/common/MarqueeStrip";
import { Reveal } from "@components/common/Reveal";
import { BrandMarkIcon } from "vectors";

const FOOTER_MARQUEE_ITEMS = [
  "Guangzhou → Tema",
  "Full tracking on every shipment",
  "Sea freight 35–50 days",
  "Air freight 5–10 days",
  "Consolidated shipping available",
] as const;

const BackToTopButton = () => {
  const [hovered, setHovered] = useState(false);

  const onClick = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const onMouseEnter = () => setHovered(true);
  const onMouseLeave = () => setHovered(false);

  return (
    <Magnetic>
      <Flex
        as="button"
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        boxSize="48px"
        borderRadius="full"
        borderWidth="1px"
        borderColor="border.light"
        bg="surface.muted"
        align="center"
        justify="center"
        cursor="pointer"
        aria-label="Back to top"
      >
        <Box
          as={LuArrowUp}
          boxSize={5}
          color="fg"
          transform={hovered ? "translateY(-3px)" : "translateY(0)"}
          transition="transform 0.25s ease-out"
        />
      </Flex>
    </Magnetic>
  );
};

/** Shared cinematic footer for the homepage and standalone marketing pages. */
export const SiteFooter = () => (
  <Box position="relative" overflow="hidden" bg="surface.background">
    <Box
      position="absolute"
      top="50%"
      left="50%"
      boxSize={{ base: "70vw", md: "50vw" }}
      transform="translate(-50%, -50%)"
      borderRadius="full"
      bg="fg"
      opacity={0.06}
      filter="blur(90px)"
      animation="breathe 8s ease-in-out infinite alternate"
      pointerEvents="none"
      zIndex={0}
    />

    <Reveal
      from="translateY(40px) scale(0.85)"
      position="absolute"
      bottom={0}
      left="50%"
      transform="translateX(-50%)"
      zIndex={0}
      pointerEvents="none"
      w="full"
      textAlign="center"
    >
      <Text
        fontSize={{ base: "7xl", md: "10rem", lg: "13rem" }}
        fontWeight="bold"
        letterSpacing="tight"
        color="fg"
        opacity={0.05}
        lineHeight="1"
        userSelect="none"
      >
        ELK CARGO
      </Text>
    </Reveal>

    <MarqueeStrip
      items={FOOTER_MARQUEE_ITEMS}
      position="relative"
      zIndex={1}
      borderTopWidth="1px"
      borderBottomWidth="1px"
      borderColor="border.light"
      py={3}
      fontSize="xs"
      fontWeight="semibold"
      textTransform="uppercase"
      letterSpacing="wider"
      color="text.secondary"
    />

    <Flex
      position="relative"
      zIndex={1}
      justifyContent="space-between"
      px={{ base: 6, md: 12, lg: 16 }}
      py={{ base: 16, md: 20 }}
      flexWrap="wrap"
      gap={{ base: 12, md: 16 }}
      maxW="7xl"
      mx="auto"
    >
      <Reveal delay={0}>
        <Flex flexDirection="column" gap={3} maxW="md">
          <BrandMarkIcon color="fg" width="150px" height="auto" />
          <Heading size="md" color="fg" fontWeight="bold">
            About Elk Cargo
          </Heading>
          <Text fontSize="md" color="text.secondary" lineHeight="tall">
            Travel coordination, freight shipping, and supply chain consulting. From
            China to your door.
          </Text>
        </Flex>
      </Reveal>
      <Reveal delay={100}>
        <Flex flexDirection="column" gap={3}>
          <Heading size="md" color="fg" fontWeight="bold">
            Quick links
          </Heading>
          <Flex flexDirection="column" gap={2}>
            <Link
              as={NextLink}
              href="/how-it-works"
              fontSize="md"
              fontWeight="medium"
              color="fg"
              _hover={{ color: "text.secondary" }}
            >
              How it works
            </Link>
            <Link
              as={NextLink}
              href="/track"
              fontSize="md"
              fontWeight="medium"
              color="fg"
              _hover={{ color: "text.secondary" }}
            >
              Track your shipment
            </Link>
            <Link
              as={NextLink}
              href="/services"
              fontSize="md"
              fontWeight="medium"
              color="fg"
              _hover={{ color: "text.secondary" }}
            >
              Ship with us
            </Link>
            <Link
              as={NextLink}
              href="/terms"
              fontSize="md"
              fontWeight="medium"
              color="fg"
              _hover={{ color: "text.secondary" }}
            >
              Terms of use
            </Link>
          </Flex>
        </Flex>
      </Reveal>
      <Reveal delay={200}>
        <Flex flexDirection="column" gap={3}>
          <Heading size="md" color="fg" fontWeight="bold">
            Contact
          </Heading>
          <Link
            href="mailto:contact@elklogistics.com"
            fontSize="md"
            fontWeight="medium"
            color="fg"
            _hover={{ color: "text.secondary" }}
          >
            elkcargo@zekeholdings.com
          </Link>
        </Flex>
      </Reveal>
      <Reveal delay={300}>
        <Flex flexDirection="column" gap={3}>
          <Heading size="md" color="fg" fontWeight="bold">
            Socials
          </Heading>
          <Flex flexDirection="column" gap={2}>
            <Magnetic strength={0.5}>
              <Link
                href="https://www.instagram.com/elkcargo/"
                target="_blank"
                fontSize="md"
                fontWeight="medium"
                color="fg"
                _hover={{ color: "text.secondary" }}
              >
                Instagram
              </Link>
            </Magnetic>
          </Flex>
        </Flex>
      </Reveal>
    </Flex>

    <Reveal position="relative" zIndex={1} delay={150}>
      <Flex
        justify="space-between"
        align="center"
        px={{ base: 6, md: 12, lg: 16 }}
        pb={10}
        maxW="7xl"
        mx="auto"
        flexWrap="wrap"
        gap={4}
      >
        <Flex
          align="center"
          gap={2}
          fontSize="xs"
          color="text.secondary"
          fontWeight="semibold"
          textTransform="uppercase"
          letterSpacing="wider"
        >
          <Text as="span">Made with</Text>
          <Box
            as="span"
            color="red.500"
            display="inline-block"
            animation="heartbeat 2s ease-in-out infinite"
          >
            ♥
          </Box>
          <Text as="span">for Guangzhou ↔ Tema trade</Text>
        </Flex>
        <BackToTopButton />
      </Flex>
    </Reveal>
  </Box>
);
